import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Home, LayoutGrid, Cpu, BookOpen, Route, CheckSquare, GitCompareArrows, FileText, Mail, ReceiptText, FileSpreadsheet, Settings, Moon, Sun, LogOut } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const navItems = [
  { id: "home", icon: Home, path: "/a/dashboard", label: "Dashboard" },
  { id: "agents", icon: Cpu, path: "/a/agents", label: "AI Agents" },
  { id: "scenarios", icon: BookOpen, path: "/a/scenarios", label: "Scenarios & Playbook" },
  { id: "einvoice", icon: ReceiptText, path: "/a/e-invoice", label: "Generate e-Invoice" },
  { id: "status", icon: Route, path: "/a/status", label: "Status & Trace" },
  { id: "batch", icon: CheckSquare, path: "/a/batch", label: "Batch Approval" },
  { id: "recon", icon: GitCompareArrows, path: "/a/reconciliation", label: "Supplier Matching" },
  { id: "vat", icon: FileSpreadsheet, path: "/a/vat-returns", label: "VAT Returns" },
  { id: "inbox", icon: Mail, path: "/a/inbox", label: "Inbox" },
  { id: "settings", icon: Settings, path: "/a/settings", label: "Settings" },
];

const Logo = () => (
  <svg width="30" height="30" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6 38 L20 8 L28 8 L14 38 Z" fill="#e11d63" />
    <path d="M20 38 L34 8 L42 8 L28 38 Z" fill="#1d4ed8" />
    <path d="M26 24 L33 24 L30 31 Z" fill="#0ea5e9" />
  </svg>
);

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [dark, setDark] = useState(false);

  const initials = (user?.name || "U").split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();

  const isActive = (path) => {
    if (path === "/a/e-invoice") return location.pathname.startsWith("/a/e-invoice") || location.pathname.startsWith("/a/parse");
    return location.pathname.startsWith(path);
  };

  const doLogout = () => { logout(); navigate("/login", { replace: true }); };

  return (
    <aside className="w-[68px] shrink-0 bg-white border-r border-gray-200 flex flex-col items-center py-4 gap-3 sticky top-0 h-screen z-30">
      <button onClick={() => navigate("/a/dashboard")} className="mb-1 hover-lift" title="InSolutions">
        <Logo />
      </button>

      <button
        onClick={() => navigate("/a/profile")}
        title={user?.company || "Profile"}
        className="w-10 h-10 rounded-xl purple-gradient text-white font-semibold text-sm flex items-center justify-center shadow-sm hover-lift"
      >
        {initials}
      </button>

      <div className="w-8 h-px bg-gray-100 my-1" />

      <nav className="flex flex-col items-center gap-2 flex-1">
        {navItems.map(({ id, icon: Icon, path, label }) => {
          const active = isActive(path);
          return (
            <button
              key={id}
              onClick={() => navigate(path)}
              title={label}
              className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-200 ${
                active ? "pink-gradient text-white shadow-md" : "text-gray-400 hover:bg-gray-100 hover:text-gray-700"
              }`}
            >
              <Icon size={20} strokeWidth={2} />
            </button>
          );
        })}
      </nav>

      <div className="flex flex-col items-center gap-2 mt-auto">
        <button
          onClick={() => setDark((d) => !d)}
          title="Toggle theme"
          className="w-10 h-10 rounded-xl flex items-center justify-center text-gray-400 hover:bg-gray-100 hover:text-gray-700 transition-colors"
        >
          {dark ? <Sun size={19} /> : <Moon size={19} />}
        </button>
        <button
          onClick={doLogout}
          title="Logout"
          className="w-10 h-10 rounded-xl flex items-center justify-center text-gray-400 hover:bg-rose-50 hover:text-rose-500 transition-colors"
        >
          <LogOut size={19} />
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
